/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-lone-blocks */
import { Button, Empty, Form, Input, Select, Spin, Table, Tabs, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import TabPane from 'antd/es/tabs/TabPane'
import Title from 'antd/es/typography/Title'
import dayjs from 'dayjs'
import {
  ArrowBigDownDash,
  ArrowBigUpDash,
  ArrowDownUp,
  Check,
  CheckCheck,
  CircleCheckBig,
  Download,
  Hourglass,
  Link,
  X,
} from 'lucide-react'
import moment from 'moment'
import { FC, SetStateAction, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

import { Railcar, RoleEnum } from '@types'

import LoadingSpinner from '../../../components/LoadingSpinner'
import RailcarsTabPane from '../../../components/RailcarsTabPane'
import {
  ActionsCellContainer,
  ContentContainer,
  FlexCol,
  FlexRow,
  StyledRow,
} from '../../../components/StyledComponents'
import { DATE_KEYS } from '../../../constants/dateKeys'
import { useUser } from '../../../contexts'
import { useFiltersV2 } from '../../../contexts/FiltersContextV2'
import {
  useBlockTrainFileTypes,
  useDestinations,
  useDownload,
  useMaterials,
  useSupplierPurchaseOrders,
} from '../../../hooks'
import useBlocktrain from '../../../hooks/blocktrain/useBlocktrain'
import useSuppliersView from '../../../hooks/useSuppliersView'
import { BaseFile, BlockTrainFileType, BlockTrainStatusEnum, RailcarDetail } from '../../../types'
import RailcarModalViewAdmin from './RailcarModalViewAdmin'
import UpdateStatusConfirm from './UpdateStatusConfirm'
import BlocktrainApi from '../../../hooks/blocktrain/api'

const { AUTHORIZED, FINISHED, LOADING, TRANSIT, UNLOADING, WAITING_APPROVAL, CANCELED } =
  BlockTrainStatusEnum

const iconByStatus = {
  [LOADING]: <ArrowBigUpDash className="icon-row" />,
  [WAITING_APPROVAL]: <Hourglass className="icon-row" />,
  [AUTHORIZED]: <Check className="icon-row" />,
  [TRANSIT]: <ArrowDownUp className="icon-row" />,
  [UNLOADING]: <ArrowBigDownDash className="icon-row" />,
  [FINISHED]: <CheckCheck className="icon-row" />,
  [CANCELED]: <X className="icon-row" />,
}

const allowedEditingStatus = [AUTHORIZED, LOADING, WAITING_APPROVAL]

const nextStatus = {
  [LOADING]: WAITING_APPROVAL,
  [WAITING_APPROVAL]: AUTHORIZED,
  [AUTHORIZED]: TRANSIT,
  [TRANSIT]: UNLOADING,
  [UNLOADING]: FINISHED,
  [FINISHED]: FINISHED,
  [CANCELED]: CANCELED,
}

const BlockTrainDetailAdmin: FC = () => {
  const { id } = useParams()
  const { t } = useTranslation()
  const { filters } = useFiltersV2()
  const { blocktrain, blocktrainIsLoading } = useBlocktrain(id!)
  const { isLoading } = useSuppliersView({ filters })
  const [selectedRailcar, setSelectedRailcar] = useState<RailcarDetail>()
  const [blocktrainStatusToUpdate, setBlocktrainStatusToUpdate] = useState<BlockTrainStatusEnum>()
  const [activeKey, setActiveKey] = useState('1')
  const [form] = Form.useForm()
  const { downloadFile } = useDownload()
  const { allDestinationsOptions } = useDestinations()
  const { fileTypes } = useBlockTrainFileTypes()
  useUser()

  const { isRole } = useUser()
  const { purchaseOrders, isLoading: purchaseOrdersIsLoading } = useSupplierPurchaseOrders()
  const { materialsOptions } = useMaterials(purchaseOrders)

  const isAdmin = isRole(RoleEnum.ADMIN)

  const handleDownloadXls = async () => {
    try {
      const blockTrain = await BlocktrainApi.exportAll(id)
      const railCars = blockTrain.railCars ?? []

      const rows = []

      rows.push([`Blocktrain ${id}`])
      rows.push([
        'Block Train',
        'Origin',
        'Destination',
        'Railcar CO:',
        'RailWayBill #:',
        'Railcars #',
        'ETA',
        'ETD',
        'Status',
      ])

      rows.push([
        blockTrain.id,
        blockTrain.origin?.city ?? '',
        blockTrain.destination?.city ?? '',
        blockTrain.trainCompany?.shortName ?? '',
        blockTrain.railCarWayBill ?? '',
        railCars.length,
        blockTrain.estimatedTimeArrival ?? '',
        blockTrain.estimatedDateOfDeparture ?? '',
        blockTrain.status ?? '',
      ])

      rows.push([])

      rows.push(['Railcars'])
      rows.push([
        '#',
        'Railcar ID',
        'Material',
        '# PO Number',
        '# Gross Weight',
        '# Tare Weight',
        '# Net Weight',
        'Delivery Note',
      ])

      railCars.forEach((rc: Railcar, index: number) => {
        const gross = Number(rc.grossTn) ?? 0
        const tare = Number(rc.tareTn) ?? 0
        const net = rc.netTn ?? gross - tare

        rows.push([
          `${blockTrain.id} - ${index + 1}`,
          rc.railCarId ?? '',
          rc.material.nameSap ?? '',
          rc.purchaseOrder?.PurchaseDocumentNumber ?? '',
          gross,
          tare,
          net,
          rc.deliveryNote ?? '',
        ])
      })

      const worksheet = XLSX.utils.aoa_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'BlockTrain')
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
      saveAs(blob, `blocktrain_${blockTrain.id}.xlsx`)
    } catch (error) {
      console.error('Error generating XLS:', error)
    }
  }

  const getBlockingStatus = useCallback(
    (blockingStatus: BlockTrainStatusEnum) => {
      if (fileTypes) {
        return fileTypes.filter(fileType => fileType.blockingStatus === blockingStatus)
      }
      return undefined
    },
    [fileTypes],
  )

  const initialValues = {
    ...blocktrain,
    origin: blocktrain?.origin.id,
    destination: blocktrain?.destination.id,
    trainCompany: blocktrain?.trainCompany.id,
  }

  const fileTypesNamesForBlockingNextStatus = useMemo(() => {
    if (blocktrain?.status) {
      const oneNextStatus = nextStatus[blocktrain.status]
      return getBlockingStatus(oneNextStatus)?.map(({ name }) => name)
    }
    return []
  }, [blocktrain, getBlockingStatus])

  const isUpdateStatusDisabled = useMemo(() => {
    const filteredFileTypes = fileTypesNamesForBlockingNextStatus
    if (blocktrain && filteredFileTypes) {
      if (!filteredFileTypes.length) {
        return false
      }

      return !filteredFileTypes.every(fileType => {
        return blocktrain.files?.some(file => file.type.name === fileType)
      })
    }
    return true
  }, [fileTypesNamesForBlockingNextStatus])

  const actionButtonDownload = (fileData: { key: string; name: string }) => {
    const handleClick = () => {
      downloadFile(fileData)
    }

    return (
      <ActionsCellContainer>
        <Button className="icon-button" onClick={handleClick}>
          <Download />
        </Button>
      </ActionsCellContainer>
    )
  }

  const columnsDocumments: ColumnsType = [
    {
      title: t('number'),
      dataIndex: 'id',
      key: 'docummentId',
      render: (docummentId: string) => docummentId ?? '-',
    },
    {
      title: t('date'),
      dataIndex: 'createAt',
      key: 'fileDate',
      render: (createAt: string) => {
        return createAt ? dayjs(createAt).format('YYYY-MM-DD HH:MM') : '-'
      },
    },
    {
      title: t('type'),
      dataIndex: 'type',
      key: 'documentType',
      render: (type: BlockTrainFileType) => type.name ?? '-',
    },
    {
      title: t('name'),
      dataIndex: 'file',
      key: 'file',
      render: (file: BaseFile) => file?.fileName ?? '-',
    },
    {
      title: t('actions'),
      key: 'commentActions',
      align: 'center',
      dataIndex: 'file',
      className: 'column-actions',
      render: (file: BaseFile) =>
        actionButtonDownload({ key: file.ticketFile, name: file.fileName }),
    },
  ]

  const handleTabChange = (key: SetStateAction<string>) => {
    setActiveKey(key)
  }

  const handleOnCancelRailcarModal = () => {
    setSelectedRailcar(undefined)
  }

  return blocktrainIsLoading || !blocktrain ? (
    <LoadingSpinner />
  ) : (
    <ContentContainer>
      <FlexCol className="blocktrain-detail__data" style={{ padding: '1rem', gap: '0.5rem' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Title level={3}>
            <span>{t('block_train')}</span> <span>{id}</span>
            <Button type="text" icon={<Link />} shape="circle" />
          </Title>
        </header>
        <Form layout="vertical" form={form} initialValues={initialValues}>
          <FlexRow className="form-flex-row">
            <Form.Item label={t('origin')} name="origin" className="form-item">
              <Select
                value={blocktrain.origin.id}
                options={[{ label: blocktrain.origin.tag, value: blocktrain.origin.id }]}
                open={false}
                onFocus={() => {}}
              />
            </Form.Item>
            <Form.Item label={t('destination')} name="destination" className="form-item">
              <Select
                value={blocktrain.destination.id}
                options={allDestinationsOptions}
                open={false}
                onFocus={() => {}}
              />
            </Form.Item>
            <Form.Item
              label={t('railcar_co')}
              name="trainCompany"
              className="form-item margin-form no-wrap-label "
            >
              <Select
                placeholder={t('select_train_company')}
                value={blocktrain.trainCompany.id}
                options={[
                  { label: blocktrain.trainCompany.shortName, value: blocktrain.trainCompany.id },
                ]}
                open={false}
                onFocus={() => {}}
              />
            </Form.Item>
            <Form.Item
              label={t('railway_bill_#')}
              name="railCarWayBill"
              className="form-item  no-wrap-label"
            >
              <Input className="input-field-railcar" readOnly />
            </Form.Item>
            <Form.Item
              label={t('railcars_#')}
              name="railCarQuantity"
              className="form-item no-wrap-label"
              initialValue={blocktrain?.railCarQuantity ?? '-'}
            >
              <Input className="input-field-railcar" readOnly />
            </Form.Item>
            <Form.Item label={t('eta')} name="eta" className="form-item">
              <div className="input-like-div-admin">
                {blocktrain?.estimatedDateOfDeparture
                  ? moment(blocktrain.estimatedTimeArrival).format(DATE_KEYS.FORMAT.YEAR_MONTH)
                  : 'N/A'}
              </div>
            </Form.Item>
            <Form.Item label={t('etd')} name="etd" className="form-item">
              <div className="input-like-div-admin">
                {blocktrain?.estimatedDateOfDeparture
                  ? moment(blocktrain.estimatedDateOfDeparture).format(DATE_KEYS.FORMAT.YEAR_MONTH)
                  : 'N/A'}
              </div>
            </Form.Item>
            <Form.Item label={t('status')} name="status" className="form-item">
              <div className="input-like-div-admin status-form-admin">
                {iconByStatus[blocktrain.status]}{' '}
                <span className="no-wrap-text">{t(`blocktrain_status.${blocktrain.status}`)}</span>
              </div>
            </Form.Item>
          </FlexRow>
        </Form>

        <FlexRow
          className="sticky-options"
          style={{
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '1rem',
            gap: '1rem',
          }}
        >
          <Tooltip
            title={
              blocktrain.railCars?.length
                ? t('download_data_to_excel')
                : t('the_download_is_not_available')
            }
            overlayClassName="centered-text-tooltip"
          >
            <Button
              type="primary"
              className="donwload-button"
              onClick={handleDownloadXls}
              disabled={!blocktrain.railCars?.length}
            >
              {t('download_to_excel')}
            </Button>
          </Tooltip>

          {blocktrain.status === BlockTrainStatusEnum.WAITING_APPROVAL && isAdmin && (
            <Button
              type="primary"
              className="form-item finish-button"
              disabled={isUpdateStatusDisabled}
              onClick={() => {
                setBlocktrainStatusToUpdate(BlockTrainStatusEnum.AUTHORIZED)
              }}
            >
              {t('authorize')}
              <CircleCheckBig style={{ marginLeft: '0.5rem' }} />
            </Button>
          )}
          {/* TODO: Botón para cambiar de TRANSIT a UNLOADING */}
          {blocktrain.status === BlockTrainStatusEnum.TRANSIT && isAdmin && (
            <Button
              type="primary"
              className="form-item finish-button"
              onClick={() => {
                setBlocktrainStatusToUpdate(BlockTrainStatusEnum.UNLOADING)
              }}
              disabled={isUpdateStatusDisabled}
            >
              {t('unloading')}
              <ArrowBigUpDash style={{ marginLeft: '0.5rem' }} />
            </Button>
          )}
          {/*  TODO: Botón para cambiar de UNLOADING a FINISHED */}
          {blocktrain.status === BlockTrainStatusEnum.UNLOADING && isAdmin && (
            <Button
              type="primary"
              className="form-item finish-button"
              onClick={() => {
                setBlocktrainStatusToUpdate(BlockTrainStatusEnum.FINISHED)
              }}
              disabled={isUpdateStatusDisabled}
            >
              {t('finished')}
              <CheckCheck style={{ marginLeft: '0.5rem' }} />
            </Button>
          )}
        </FlexRow>

        {isUpdateStatusDisabled && isAdmin ? (
          <FlexRow style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
            <span style={{ color: 'red', marginBottom: '-2rem' }}>{t('files_missing')}</span>
          </FlexRow>
        ) : null}
      </FlexCol>
      <StyledRow className="ticket-handler">
        <div style={{ flex: 1 }} />
        {isLoading ? <Spin /> : <div style={{ flex: 1 }} />}
      </StyledRow>
      <div>
        <Tabs activeKey={activeKey} onChange={handleTabChange}>
          <TabPane key="1" tab={<span className="tabs-offset">{t('railcars_tab')}</span>}>
            <RailcarsTabPane
              blocktrain={blocktrain}
              allowedEditingStatus={allowedEditingStatus}
              setSelectedRailcar={setSelectedRailcar}
              materialsOptions={materialsOptions}
              purchaseOrders={purchaseOrders}
              purchaseOrdersIsLoading={purchaseOrdersIsLoading}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                {t('documents_tab')}
                {isUpdateStatusDisabled && isAdmin && <span style={{ color: 'red' }}>*</span>}
              </span>
            }
            key="2"
          >
            {' '}
            <Table
              columns={columnsDocumments}
              dataSource={blocktrain?.files}
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: <Empty description={t('no_data_found')} />,
              }}
              scroll={{
                x: 'max-content',
              }}
            />
          </TabPane>
          {/* <TabPane tab={t('comments_tab')} key="3">
            <Table
              columns={columnsComments}
              dataSource={blocktrain?.railCars}
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: <Empty description={t('no_data_found')} />,
              }}
            />
          </TabPane> */}
        </Tabs>
      </div>
      <RailcarModalViewAdmin
        onCancel={handleOnCancelRailcarModal}
        blockTrain={blocktrain}
        railcar={selectedRailcar}
      />
      {blocktrainStatusToUpdate && (
        <UpdateStatusConfirm
          key={blocktrainStatusToUpdate}
          close={() => {
            setBlocktrainStatusToUpdate(undefined)
          }}
          status={blocktrainStatusToUpdate}
          id={blocktrain.id}
        />
      )}
    </ContentContainer>
  )
}

export default BlockTrainDetailAdmin
